import { computed, isRef, onScopeDispose, watch } from 'vue'
import { addClass, hasClass, removeClass, throwError } from '@zoryn/utils'
import { useNamespace } from '../use-namespace'

import type { Ref } from 'vue'
import type { UseNamespaceReturn } from '../use-namespace'

export type UseLockScreenOptions = {
  ns?: UseNamespaceReturn
  // shouldLock?: MaybeRef<boolean>
}

/** 按 class 维度的锁屏计数，避免多弹层或不同 namespace 互相干扰 */
const classLockCount = new Map<string, number>()
let unlockTimer: ReturnType<typeof setTimeout> | undefined

/** 测试专用：重置模块级锁屏状态 */
export function resetBodyLockForTests() {
  classLockCount.clear()
  if (unlockTimer) {
    clearTimeout(unlockTimer)
    unlockTimer = undefined
  }
}

function cancelScheduledUnlock() {
  if (unlockTimer) {
    clearTimeout(unlockTimer)
    unlockTimer = undefined
  }
}

function scheduleClassRemoval(className: string) {
  cancelScheduledUnlock()
  unlockTimer = setTimeout(() => {
    unlockTimer = undefined
    if (!classLockCount.has(className) && hasClass(document.body, className)) {
      removeClass(document.body, className)
    }
  }, 200)
}

function lockBodyClass(className: string) {
  cancelScheduledUnlock()
  const count = classLockCount.get(className) ?? 0
  if (count === 0) {
    addClass(document.body, className)
  }
  classLockCount.set(className, count + 1)
}

function unlockBodyClass(className: string) {
  const count = classLockCount.get(className) ?? 0
  const next = Math.max(0, count - 1)
  if (next === 0) {
    classLockCount.delete(className)
    scheduleClassRemoval(className)
  } else {
    classLockCount.set(className, next)
  }
}

/**
 * Hook that monitoring the ref value to lock or unlock the screen.
 * When the trigger became true, it assumes modal is now opened and vice versa.
 * @param trigger {Ref<boolean>}
 */
export const useLockscreen = (
  trigger: Ref<boolean>,
  options: UseLockScreenOptions = {}
) => {
  if (!isRef(trigger)) {
    throwError(
      '[useLockscreen]',
      'You need to pass a ref param to this function'
    )
  }

  const ns = options.ns || useNamespace('popup')

  const hiddenCls = computed(() => ns.bm('parent', 'hidden'))

  let lockedByThis = false

  const acquireBodyLock = () => {
    if (lockedByThis) {
      return
    }
    lockedByThis = true
    lockBodyClass(hiddenCls.value)
  }

  const releaseBodyLock = () => {
    if (!lockedByThis) {
      return
    }
    lockedByThis = false
    unlockBodyClass(hiddenCls.value)
  }

  watch(trigger, (val) => {
    if (val) {
      acquireBodyLock()
      return
    }
    releaseBodyLock()
  })

  onScopeDispose(() => releaseBodyLock())
}
